
import React, { createContext, useState, useContext, useEffect } from 'react';

// Types for the user and auth context
export type User = {
  id: string;
  email: string;
  name?: string;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
};

// Create the auth context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
});

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Mock user database
const MOCK_USERS = [
  {
    id: "1",
    email: "test@example.com",
    name: "Test User",
    password: "password123" // In a real app, never store passwords in plain text
  }
];

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on initial load
  useEffect(() => {
    const checkSession = () => {
      try {
        console.log("Checking session from localStorage");
        // Get current session from localStorage
        const storedUser = localStorage.getItem('givgo_user');
        
        if (storedUser) {
          console.log("Found existing user in localStorage");
          setUser(JSON.parse(storedUser));
        } else {
          console.log("No active user found in localStorage");
        }
      } catch (error) {
        console.error('Session check error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  // Sign in function with mock database
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log("Attempting to sign in:", email);
      
      // Find user in mock database
      const mockUser = MOCK_USERS.find(u => u.email === email && u.password === password);
      
      if (!mockUser) {
        throw new Error("Invalid credentials");
      }
      
      // Create user object (without password)
      const userObject = {
        id: mockUser.id,
        email: mockUser.email,
        name: mockUser.name
      };
      
      // Store in localStorage
      localStorage.setItem('givgo_user', JSON.stringify(userObject));
      setUser(userObject);
      console.log("Login successful for:", email);
      
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign up function with mock database
  const signUp = async (email: string, password: string, name: string) => {
    try {
      setLoading(true);
      console.log("Attempting to create account for:", email);
      
      // Check if user already exists
      if (MOCK_USERS.some(u => u.email === email)) {
        throw new Error("User already exists");
      }
      
      // Create new user ID
      const newId = (MOCK_USERS.length + 1).toString();
      
      // Create new user
      const newUser = {
        id: newId,
        email,
        name,
        password
      };
      
      // Add to mock database
      MOCK_USERS.push(newUser);
      
      // Create user object (without password)
      const userObject = {
        id: newUser.id,
        email: newUser.email,
        name: newUser.name
      };
      
      // Store in localStorage
      localStorage.setItem('givgo_user', JSON.stringify(userObject));
      setUser(userObject);
      console.log("Account created for:", email);
      
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      setLoading(true);
      localStorage.removeItem('givgo_user');
      setUser(null);
      console.log("User signed out");
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
